import axios, { AxiosPromise, AxiosRequestConfig, AxiosInstance } from 'axios';

import API, { apiConfig, getConfig } from '@library/api';

import CataloguesType from '@migrated/context/types/catalogues';
import AvailableBooksType from '@migrated/context/types/availableBooks';
import TickerType from '@migrated/context/types/ticker';

import { type AnyAxiosRequestConfig } from './types';
import type AccountStatusType from '@src/migrated-bundle-components/context/types/accountStatus';
import type UserSettingsType from '@src/migrated-bundle-components/context/types/userSettings';

export interface WithPayload<PayloadType> {
  payload: PayloadType;
}

export interface GenericAPIGet<PayloadType> {
  (options?: AxiosRequestConfig): AxiosPromise<WithPayload<PayloadType>>;
}

const getAccountStatus: GenericAPIGet<AccountStatusType> = options => API.get('account_status', options);

const getSettings: GenericAPIGet<UserSettingsType> = options => API.get('settings', options);

const getCatalogues: GenericAPIGet<CataloguesType> = options => API.get('catalogues', options);

const getCataloguesCurrencies: GenericAPIGet<CataloguesType> = options => API.get('catalogues/currencies', options);

const getCataloguesLocales: GenericAPIGet<CataloguesType> = options => API.get('catalogues/locales', options);

const getAvailableBooks: GenericAPIGet<AvailableBooksType> = options => API.get('available_books', options);

const getTicker = ({ params }: { params: Record<string, unknown> }): AxiosPromise<WithPayload<TickerType>> =>
  API.get<WithPayload<TickerType>>('ticker', { params });

// @TODO Move type for another file

type PublicCurrencyInfo = {
  identifier: string;
  tiles: Array<{
    tile_type: string;
    request_info?: {
      available_periods: {
        default_url: string;
        default_selection: string;
        parameter: string;
        periods: Array<{
          query: string;
          period: string;
          url: string;
        }>;
      };
    };
    title?: string;
    text?: string;
    footer?: unknown;
    learn_more_url?: string;
    content?: Array<{
      metric: string;
      value: string;
      description: string;
      currency_code?: string;
    }>;
  }>;
};
const getPublicCurrency = async ({
  params,
}: {
  params: Record<string, unknown>;
}): Promise<{ payload: PublicCurrencyInfo }> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_HOST}/api/v3/currency_info_public?currencyCode=${params.currencyCode}&preferredCurrency=${params.preferredCurrency}&language=${params.language}`,
  );
  if (!response.ok) {
    throw response;
  }
  return await response.json();
};

export type PublicCurrencyGraph = {
  currency: {
    code: string;
    full_name: string;
    color: string;
    precision: number;
    type: string;
  };
  last_price: {
    price: string;
    currency: {
      code: string;
      full_name: string;
      color: string;
      precision: number;
      type: string;
    };
  };
  graph_data: {
    percentage_change: string;
    period: string;
    period_description: string;
    period_last_update: string;
    period_date_format: string;
    points: Array<{
      date: number;
      number: string;
    }>;
    precision_rules: Array<{
      max: number;
      precision: number;
    }>;
  };
};

const getPublicCurrencyGraph = async ({
  params,
}: {
  params: Record<string, unknown>;
}): Promise<{ payload: PublicCurrencyGraph }> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_HOST}/api/v3/currency_graph_public?currencyCode=${params.currencyCode}&period=${params.period}&preferredCurrency=${params.preferredCurrency}&language=${params.language}`,
  );
  if (!response.ok) {
    throw response;
  }
  return await response.json();
};

export type Root = Root2[];

export interface Root2 {
  identifier: string;
  tiles: Tile[];
}

export interface Tile {
  tile_type: string;
  request_info?: RequestInfo;
  title?: string;
  text?: string;
  footer?: Footer;
  learn_more_url?: string;
  content?: Content[];
}

export interface RequestInfo {
  available_periods: AvailablePeriods;
}

export interface AvailablePeriods {
  default_url: string;
  default_selection: string;
  parameter: string;
  periods: Period[];
}

export interface Period {
  query: string;
  period: string;
  url: string;
}

export interface Footer {
  urls: Url[];
}

export interface Url {
  title: string;
  url: string;
}

export interface Content {
  metric: string;
  value: string;
  description: string;
  currency_code?: string;
}

export type PricesServiceTable = {
  canvas: Array<{
    identifier: string;
    tiles: Array<{
      tile_type: string;
      request_info?: {
        available_periods: {
          default_url: string;
          default_selection: string;
          parameter: string;
          periods: Array<{ query: string; period: string; url: string }>;
        };
      };
      title?: string;
      text?: string;
      footer?: {
        urls: Array<{
          title: string;
          url: string;
        }>;
      };
      learn_more_url?: string;
      content?: Array<{
        metric: string;
        value: string;
        description: string;
        currency_code?: string;
      }>;
    }>;
  }>;
  graph: Array<{
    currency: {
      code: string;
      full_name: string;
      color: string;
      precision: number;
      type: string;
    };
    last_price: {
      price: string;
      currency: {
        code: string;
        full_name: string;
        color: string;
        precision: number;
        type: string;
      };
    };
    graph_data: {
      percentage_change: string;
      period: string;
      period_description: string;
      period_last_update: string;
      period_date_format: string;
      points: Array<{
        date: number;
        number: string;
      }>;
      precision_rules: Array<{
        max: number;
        precision: number;
      }>;
    };
  }>;
  page_size: number;
  total_count: number;
};

const getPricesServiceTable = async ({
  params,
}: {
  params: Record<string, unknown>;
}): Promise<{ payload: PricesServiceTable }> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_HOST}/api/v3/prices-service/table?period=${params.period}&preferredCurrency=${params.preferredCurrency}&language=${params.language}&start=${params.start}&limit=${params.limit}`,
  );
  if (!response.ok) {
    throw response;
  }
  return await response.json();
};

type CompareExchangeRates = {
  prices: Array<{
    exchange: string;
    fee: number | null;
    rate: number | null;
    last_updated: string;
    currency: string;
  }>;
};

const getCompareExchangeRates = async (): Promise<{ payload: CompareExchangeRates }> => {
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_HOST}/api/v3/compare_exchange_rates`);
  if (!response.ok) {
    throw response;
  }
  return await response.json();
};

type UserBalance = {
  balances: Array<{
    currency: string;
    available: string;
    locked: string;
    total: string;
    pending_deposit: string;
    pending_withdrawal: string;
  }>;
};

const getUserBalance = async (): Promise<{ data: { payload: UserBalance } }> => {
  return API.get(`balance`, { params: { use_preferred_currency: 1 } });
};

type UserCombinedBalance = {
  balances: Array<{
    available_amount: string;
    converted_amount: string;
    currency_code: string;
    currency_full_name: string;
    is_fiat: boolean;
    portfolio_percentage: string;
    total_amount: string;
  }>;
};

const getUserCombinedBalance = async (): Promise<{ data: { payload: UserCombinedBalance } }> => {
  return API.get(`combined_balance`, { params: { use_preferred_currency: 1 } });
};

const getRateConversion = async () => {
  try {
    const response = await API.get(`/get_exchange_rates/ars`);
    return response.data.payload;

  } catch (error) {
    return null;

  }
};

type CurrencyConversion = {
  id: string;
  from_amount: string;
  from_currency: string;
  to_amount: string;
  to_currency: string;
  estimated_slippage: {
    level: string;
    message: string;
    value: string;
  };
  created: number;
  expires: number;
  rate: string;
  rate_currency: string;
  padding: string;
  direction: string;
  book: string;
  status: string;
  oid: string;
  next_recurrent_events: {
    DAILY: number;
    WEEKLY: number;
    MONTHLY: number;
  };
};

const getCurrencyConversion = async ({
  fromCurrency,
  amount,
  toCurrency,
}: {
  fromCurrency: string;
  amount: string;
  toCurrency: string;
}): Promise<CurrencyConversion> => {
  const {
    data: { payload: currencyConversion },
  } = await API.post('/currency_conversions', {
    from_currency: fromCurrency,
    receive_amount: amount,
    to_currency: toCurrency,
  });

  return await currencyConversion;
};

export {
  getAccountStatus,
  getAvailableBooks,
  getCatalogues,
  getCataloguesCurrencies,
  getCataloguesLocales,
  getCompareExchangeRates,
  getCurrencyConversion,
  getPricesServiceTable,
  getPublicCurrency,
  getPublicCurrencyGraph,
  getRateConversion,
  getSettings,
  getTicker,
  getUserBalance,
  getUserCombinedBalance,
};
